/**
 * Math Detection Utility
 *
 * Detects mathematical notation in text content to determine whether
 * it should be rendered with LaTeX/KaTeX or as plain text.
 */

/**
 * Content type classification for rendering decisions
 */
export type ContentType = 'pure-text' | 'pure-math' | 'mixed-content';

/**
 * Result of content analysis
 */
export interface ContentAnalysis {
  type: ContentType;
  hasMath: boolean;
  confidence: 'low' | 'medium' | 'high';
  shouldUseMathRenderer: boolean;
  recommendedStrategy: 'plain-text' | 'full-math' | 'parse-mixed';
}

/**
 * Determines if a given text string contains mathematical notation
 * that would benefit from LaTeX rendering.
 *
 * @param text The text to analyze for mathematical content
 * @returns true if math notation is detected, false otherwise
 */
export function containsMath(text: string): boolean {
  const analysis = analyzeContent(text);
  return analysis.hasMath;
}

/**
 * Analyzes content to determine the best rendering strategy
 *
 * @param text The text to analyze
 * @returns Detailed analysis of the content type and recommended rendering approach
 */
export function analyzeContent(text: string): ContentAnalysis {
  if (!text || typeof text !== 'string') {
    return {
      type: 'pure-text',
      hasMath: false,
      confidence: 'high',
      shouldUseMathRenderer: false,
      recommendedStrategy: 'plain-text'
    };
  }

  const trimmedText = text.trim();
  if (trimmedText.length === 0) {
    return {
      type: 'pure-text',
      hasMath: false,
      confidence: 'high',
      shouldUseMathRenderer: false,
      recommendedStrategy: 'plain-text'
    };
  }

  // Math patterns to detect
  const mathPatterns = [
    // Basic math operators in meaningful contexts
    /[=+\-*/^]/,
    // Subscripts and superscripts (Unicode)
    /[₀-₉⁰-⁹]/,
    // Mathematical symbols
    /[√∫∑∏∆∇]/,
    // Coordinate notation like (x₁, y₂) or (0, 2)
    /\([xy]\d*\s*,\s*[xy]?\d*\)/,
    /\(\d+\s*,\s*\d+\)/,
    // Equation patterns like "y = 2x + 1" or "f(x) = x^2"
    /[xy]\s*=\s*[^,\s]+/,
    /f\([xy]\)\s*=/,
    // Fractions like "1/2", "-1/2"
    /-?\d+\/\d+/,
    // Function notation
    /\b(sin|cos|tan|log|ln|exp|sqrt)\s*\(/,
    // Mathematical expressions with parentheses and operations
    /\([^)]*[+\-*^][^)]*\)/,
    // Square root notation
    /√\[.*\]|√\(.*\)/,
    // Distance/slope formulas with specific patterns
    /\([xy]₂?\s*[-+]\s*[xy]₁?\).*/,
    // Degree notation
    /\d+°/,
  ];

  const hasMath = mathPatterns.some(pattern => pattern.test(trimmedText));

  if (!hasMath) {
    return {
      type: 'pure-text',
      hasMath: false,
      confidence: 'high',
      shouldUseMathRenderer: false,
      recommendedStrategy: 'plain-text'
    };
  }

  // Analyze if this is pure math or mixed content
  const isPureMath = isPureMathExpression(trimmedText);

  if (isPureMath) {
    return {
      type: 'pure-math',
      hasMath: true,
      confidence: 'high',
      shouldUseMathRenderer: true,
      recommendedStrategy: 'full-math'
    };
  } else {
    return {
      type: 'mixed-content',
      hasMath: true,
      confidence: 'medium',
      shouldUseMathRenderer: true,
      recommendedStrategy: 'parse-mixed'
    };
  }
}

/**
 * Determines if text is a pure mathematical expression (no mixed content)
 */
function isPureMathExpression(text: string): boolean {
  // Common indicators of mixed content (English sentence starters/words)
  const mixedContentIndicators = [
    /^\s*(find|what|if|when|given|using|calculate|solve|determine)/i,
    /^\s*(the|a|an)\s+/i,
    /\?\s*$/,  // Ends with question mark
    /\b(is|are|was|were|will|would|should|could|can|may|might)\b/i,
    /\b(where|when|what|how|why|which)\b/i,
    /\b(value|answer|solution|result|slope|intercept|equation)\b/i,
  ];

  // If it contains mixed content indicators, it's not pure math
  if (mixedContentIndicators.some(pattern => pattern.test(text))) {
    return false;
  }

  // Check if it's predominantly mathematical
  const wordCount = text.split(/\s+/).length;
  const mathSymbolCount = (text.match(/[=+\-*/^₀-₉⁰-⁹√∫∑∏∆∇()]/g) || []).length;

  // Special case: Check if it's a standalone equation pattern
  const standaloneEquationPatterns = [
    /^[a-zA-Z][₀-₉⁰-⁹]?\s*[=]\s*[^,.\s?!]*(?:\s*[+\-]\s*[^,.\s?!]*)*$/,
    /^[a-zA-Z]\d*[₀-₉⁰-⁹]+$/,  // Just a variable with superscript/subscript
    /^-?\d+\/\d+$/,  // Just a fraction
    /^\([xy]?\d*[₀-₉]?\s*,\s*[xy]?\d*[₀-₉]?\)$/,  // Just coordinates
  ];

  if (standaloneEquationPatterns.some(pattern => pattern.test(text))) {
    return true;
  }

  // If short and has math symbols, likely pure math
  if (wordCount <= 3 && mathSymbolCount > 0) {
    return true;
  }

  // If more math symbols than words, likely pure math
  if (mathSymbolCount >= wordCount) {
    return true;
  }

  // Otherwise, it's mixed content
  return false;
}

/**
 * Represents a segment of text that is either pure text or math
 */
export interface TextSegment {
  type: 'text' | 'math';
  content: string;
  originalText: string;
}

/**
 * Parses mixed content into segments that can be rendered separately
 *
 * @param text The mixed content to parse
 * @returns Array of segments with their types
 */
export function parseMixedContent(text: string): TextSegment[] {
  if (!text || typeof text !== 'string') {
    return [];
  }

  // For now, implement a simple strategy:
  // 1. Identify mathematical expressions within the text
  // 2. Split around them while preserving them

  const segments: TextSegment[] = [];
  let currentIndex = 0;
  const originalText = text;

  // Patterns for mathematical expressions that should be rendered as math
  const mathExpressionPatterns = [
    // Full equations with coefficients like "3x + 7 = 22", "2y - 5 = 13"
    /\d*[a-zA-Z]\d*\s*[+\-*/^]\s*[^,.\s?!]+\s*=\s*[^,.\s?!]+/g,
    // Simple equations like "x = 5", "y = 2x + 1"
    /\b[a-zA-Z][₀-₉⁰-⁹]?\s*=\s*[^,.\s?!]+/g,
    // Variables with coefficients like "3x", "2y", "-5z"
    /-?\d+[a-zA-Z]\d*/g,
    // Variables with superscripts/subscripts like "x²", "y₁"
    /\b[a-zA-Z]\d*[₀-₉⁰-⁹]+/g,
    // Fractions like "1/2", "-3/4"
    /-?\d+\/\d+/g,
    // Coordinate pairs like "(3, 4)", "(x₁, y₂)"
    /\([xy]?\d*[₀-₉]?\s*,\s*[xy]?\d*[₀-₉]?\)/g,
    // Mathematical functions
    /\b(sin|cos|tan|log|ln|exp|sqrt)\s*\([^)]+\)/g,
    // Mathematical formulas with symbols
    /[√∫∑∏∆∇][^.\s,]*/g,
    // Complex expressions in parentheses with operations
    /\([^)]*[+\-*/^=][^)]*\)/g,
  ];

  // Find all math expressions
  const mathMatches: Array<{ match: string; start: number; end: number }> = [];

  mathExpressionPatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      mathMatches.push({
        match: match[0],
        start: match.index,
        end: match.index + match[0].length
      });
    }
  });

  // Sort matches by position
  mathMatches.sort((a, b) => a.start - b.start);

  // Remove overlapping matches (keep the first one)
  const nonOverlappingMatches = [];
  let lastEnd = -1;
  for (const match of mathMatches) {
    if (match.start >= lastEnd) {
      nonOverlappingMatches.push(match);
      lastEnd = match.end;
    }
  }

  // Build segments
  if (nonOverlappingMatches.length === 0) {
    // No math found, return as single text segment
    return [{
      type: 'text',
      content: text,
      originalText
    }];
  }

  // Add text segments and math segments
  nonOverlappingMatches.forEach((match, index) => {
    // Add text before this math expression
    if (match.start > currentIndex) {
      const textContent = text.slice(currentIndex, match.start);
      // Always add text segments, even if they're just whitespace
      // This preserves spacing between math expressions
      segments.push({
        type: 'text',
        content: textContent,
        originalText
      });
    }

    // Add the math expression
    segments.push({
      type: 'math',
      content: match.match,
      originalText
    });

    currentIndex = match.end;
  });

  // Add remaining text after the last math expression
  if (currentIndex < text.length) {
    const textContent = text.slice(currentIndex);
    // Always add text segments, even if they're just whitespace
    segments.push({
      type: 'text',
      content: textContent,
      originalText
    });
  }

  return segments;
}

/**
 * Gets information about the detected math content in text
 * Useful for debugging and analytics
 *
 * @param text The text to analyze
 * @returns Object with details about detected math patterns
 */
export function getMathInfo(text: string): {
  hasMath: boolean;
  detectedPatterns: string[];
  confidence: 'low' | 'medium' | 'high';
} {
  if (!text || typeof text !== 'string') {
    return {
      hasMath: false,
      detectedPatterns: [],
      confidence: 'low'
    };
  }

  const patterns = [
    { name: 'operators', regex: /[=+\-*^]/, weight: 1 },
    { name: 'subscripts', regex: /[₀-₉⁰-⁹]/, weight: 3 },
    { name: 'symbols', regex: /[√∫∑∏∆∇]/, weight: 3 },
    { name: 'coordinates', regex: /\(\d+\s*,\s*\d+\)/, weight: 2 },
    { name: 'equations', regex: /[xy]\s*=\s*[^,\s]+/, weight: 3 },
    { name: 'fractions', regex: /-?\d+\/\d+/, weight: 2 },
    { name: 'functions', regex: /\b(sin|cos|tan|log|ln|exp|sqrt)\s*\(/, weight: 3 }
  ];

  const detectedPatterns: string[] = [];
  let totalWeight = 0;

  patterns.forEach(({ name, regex, weight }) => {
    if (regex.test(text)) {
      detectedPatterns.push(name);
      totalWeight += weight;
    }
  });

  const hasMath = detectedPatterns.length > 0;
  let confidence: 'low' | 'medium' | 'high' = 'low';

  if (totalWeight >= 5) {
    confidence = 'high';
  } else if (totalWeight >= 2) {
    confidence = 'medium';
  }

  return {
    hasMath,
    detectedPatterns,
    confidence
  };
}